/**
 * Notificaties: wat anderen in dit bouwnummer hebben gewijzigd sinds je de vorige
 * keer keek.
 *
 * De bron is het wijzigingslog van het gekozen bouwnummer; eigen wijzigingen staan er
 * niet tussen, die ken je al. "Gelezen" is per apparaat: het moment waarop het paneel
 * het laatst is gesloten. Het getal in de zijbalk telt de regels van daarna.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { api, type AuditRegel } from '../api';
import { datumTijd, useGegevens } from '../gegevens';
import { IconKruis } from './Iconen';
import { actieLabel } from './Wijzigingslog';

const GELEZEN = 'houtstaat.notificatiesGelezen';
const MAX = 20;

const leesGelezen = (): string => {
  try {
    return localStorage.getItem(GELEZEN) ?? '';
  } catch {
    return '';
  }
};

export function useNotificaties() {
  const { boot, ik, mag, wijzigingsteller } = useGegevens();
  const [regels, setRegels] = useState<AuditRegel[]>([]);
  const [gelezen, setGelezen] = useState(leesGelezen);

  useEffect(() => {
    // Het wijzigingslog valt onder de houtstaat; een rol zonder dat recht krijgt geen meldingen.
    if (!boot || !ik || !mag('houtstaatBekijken')) return;
    let actueel = true;
    api
      .audit(boot.bouwnummer)
      .then((log) => {
        if (actueel) setRegels(log.filter((r) => r.gebruiker !== ik.naam).slice(0, MAX));
      })
      .catch(() => undefined);
    return () => {
      actueel = false;
    };
  }, [boot, ik, mag, wijzigingsteller]);

  const markeerGelezen = useCallback(() => {
    const nu = new Date().toISOString();
    setGelezen(nu);
    try {
      localStorage.setItem(GELEZEN, nu);
    } catch {
      // Zonder opslag komt het getal na herladen terug; verder werkt het.
    }
  }, []);

  const ongelezen = regels.filter((r) => r.tijdstip > gelezen).length;
  return { regels, gelezen, ongelezen, markeerGelezen };
}

/** Het paneel naast de knop in de zijbalk. Sluit met Escape en met een klik ernaast. */
export function NotificatiesPaneel({
  knop,
  open,
  onSluit,
  regels,
  gelezen,
}: {
  knop: HTMLElement | null;
  open: boolean;
  onSluit: () => void;
  regels: AuditRegel[];
  gelezen: string;
}) {
  const paneel = useRef<HTMLDivElement>(null);
  const [plek, setPlek] = useState<{ left: number; bottom: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !knop) return;
    const r = knop.getBoundingClientRect();
    // Rechts naast de zijbalk, met de onderkant op de hoogte van de knop. Past dat
    // niet (schuiflade op een smal scherm), dan over de knop heen.
    const left = r.right + 12 + 360 <= window.innerWidth ? r.right + 12 : 12;
    setPlek({ left, bottom: Math.max(12, window.innerHeight - r.bottom) });
  }, [open, knop]);

  useEffect(() => {
    if (!open) return;
    const opToets = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onSluit();
    };
    const opKlik = (e: MouseEvent) => {
      const doel = e.target as Node;
      if (!paneel.current?.contains(doel) && !knop?.contains(doel)) onSluit();
    };
    window.addEventListener('keydown', opToets);
    window.addEventListener('mousedown', opKlik);
    paneel.current?.focus();
    return () => {
      window.removeEventListener('keydown', opToets);
      window.removeEventListener('mousedown', opKlik);
    };
  }, [open, knop, onSluit]);

  if (!open || !plek) return null;

  return (
    <div
      ref={paneel}
      className="hs-notificaties"
      role="dialog"
      aria-label="Notificaties"
      tabIndex={-1}
      style={{ left: plek.left, bottom: plek.bottom }}
    >
      <div className="hs-notificaties__kop">
        <p className="hs-annotatie">Notificaties</p>
        <button type="button" className="hs-icoonknop" onClick={onSluit} aria-label="Sluiten">
          <IconKruis maat={18} />
        </button>
      </div>
      {regels.length === 0 ? (
        <p className="hs-notificaties__leeg">Geen wijzigingen van anderen in dit bouwnummer.</p>
      ) : (
        <ul className="hs-notificaties__lijst">
          {regels.map((r) => (
            <li key={r.id} className={r.tijdstip > gelezen ? 'nieuw' : undefined}>
              <p className="wat">
                {actieLabel(r.actie)} <span className="hs-mono">{r.onderwerp}</span>
              </p>
              <p className="wie">
                {r.gebruiker} · {datumTijd(r.tijdstip)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
